import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  VaultMemberResponseDto,
  VaultMemberCreateDto,
  VaultMemberUpdateDto,
  MemberVaultListItemDto,
} from '../../types/api';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../config/api';

export type VaultMemberItem = VaultMemberResponseDto;

export interface VaultMemberState {
  loading: boolean;
  items: VaultMemberItem[];
  /** Vaults the current user is a member of (from GET /api/VaultMember/me) */
  meVaults: MemberVaultListItemDto[];
  /** Pending vault invites (from GET /api/VaultMember/me/pending) */
  mePendingVaults: MemberVaultListItemDto[];
  error: string | null;
  selectedVaultId: string | null;
  selectedUserId: string | null;
  isModalOpen: boolean;
  modalMode: 'add' | 'edit';
  operationLoading: {
    list: boolean;
    get: boolean;
    me: boolean;
    mePending: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  errors: {
    list: string | null;
    get: string | null;
    me: string | null;
    mePending: string | null;
    create: string | null;
    update: string | null;
    delete: string | null;
  };
}

const initialOperationLoading = {
  list: false,
  get: false,
  me: false,
  mePending: false,
  create: false,
  update: false,
  delete: false,
};

const initialErrors = {
  list: null as string | null,
  get: null as string | null,
  me: null as string | null,
  mePending: null as string | null,
  create: null as string | null,
  update: null as string | null,
  delete: null as string | null,
};

const initialState: VaultMemberState = {
  loading: false,
  items: [],
  meVaults: [],
  mePendingVaults: [],
  error: null,
  selectedVaultId: null,
  selectedUserId: null,
  isModalOpen: false,
  modalMode: 'add',
  operationLoading: { ...initialOperationLoading },
  errors: { ...initialErrors },
};

export const getVaultMembers = createAsyncThunk(
  'vaultMember/getVaultMembers',
  async (vaultId: string, { rejectWithValue }) => {
    try {
      const items = await apiGet<VaultMemberItem[]>(`/api/VaultMember/vault/${vaultId}`);
      return { vaultId, items };
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Üyeler yüklenirken hata oluştu');
    }
  }
);

export const getVaultMember = createAsyncThunk(
  'vaultMember/getVaultMember',
  async (
    { vaultId, userId }: { vaultId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      return await apiGet<VaultMemberItem>(
        `/api/VaultMember/vault/${vaultId}/user/${userId}`
      );
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Üye yüklenirken hata oluştu');
    }
  }
);

/** GET /api/VaultMember/me — vaults the current user is a member of */
export const getVaultMemberMe = createAsyncThunk(
  'vaultMember/getVaultMemberMe',
  async (_, { rejectWithValue }) => {
    try {
      return await apiGet<MemberVaultListItemDto[]>('/api/VaultMember/me');
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Kasa listesi yüklenirken hata oluştu');
    }
  }
);

/** GET /api/VaultMember/me/pending — pending vault invites for current user */
export const getVaultMemberMePending = createAsyncThunk(
  'vaultMember/getVaultMemberMePending',
  async (_, { rejectWithValue }) => {
    try {
      return await apiGet<MemberVaultListItemDto[]>('/api/VaultMember/me/pending');
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Bekleyen davetler yüklenirken hata oluştu');
    }
  }
);

/** POST /api/VaultMember/me/accept/{vaultId} — accept vault invite */
export const acceptVaultMemberInvite = createAsyncThunk(
  'vaultMember/acceptVaultMemberInvite',
  async (vaultId: string, { rejectWithValue }) => {
    try {
      return await apiPost<VaultMemberItem>(`/api/VaultMember/me/accept/${vaultId}`, {});
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Davet kabul edilirken hata oluştu');
    }
  }
);

/** POST /api/VaultMember/me/reject/{vaultId} — reject vault invite */
export const rejectVaultMemberInvite = createAsyncThunk(
  'vaultMember/rejectVaultMemberInvite',
  async (vaultId: string, { rejectWithValue }) => {
    try {
      return await apiPost<VaultMemberItem>(`/api/VaultMember/me/reject/${vaultId}`, {});
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Davet reddedilirken hata oluştu');
    }
  }
);

export const createVaultMember = createAsyncThunk(
  'vaultMember/createVaultMember',
  async (body: VaultMemberCreateDto, { rejectWithValue }) => {
    try {
      return await apiPost<VaultMemberItem>('/api/VaultMember', body);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Üye eklenirken hata oluştu');
    }
  }
);

export const updateVaultMember = createAsyncThunk(
  'vaultMember/updateVaultMember',
  async (
    {
      vaultId,
      userId,
      body,
    }: { vaultId: string; userId: string; body: VaultMemberUpdateDto },
    { rejectWithValue }
  ) => {
    try {
      const item = await apiPatch<VaultMemberItem>(
        `/api/VaultMember/vault/${vaultId}/user/${userId}`,
        body
      );
      return { vaultId, userId, item };
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Üye güncellenirken hata oluştu');
    }
  }
);

export const deleteVaultMember = createAsyncThunk(
  'vaultMember/deleteVaultMember',
  async (
    { vaultId, userId }: { vaultId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      await apiDelete(`/api/VaultMember/vault/${vaultId}/user/${userId}`);
      return { vaultId, userId };
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Üye kaldırılırken hata oluştu');
    }
  }
);

/** DELETE /api/VaultMember/vault/{vaultId}/user?userName=... — remove member by userName */
export const removeVaultMemberByUserName = createAsyncThunk(
  'vaultMember/removeVaultMemberByUserName',
  async (
    { vaultId, userName }: { vaultId: string; userName: string },
    { rejectWithValue }
  ) => {
    try {
      const path = `/api/VaultMember/vault/${encodeURIComponent(vaultId)}/user?userName=${encodeURIComponent(userName)}`;
      await apiDelete(path);
      return { vaultId, userName };
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Üye kaldırılırken hata oluştu');
    }
  }
);

const vaultMemberSlice = createSlice({
  name: 'vaultMember',
  initialState,
  reducers: {
    setSelectedVaultIdForMembers: (state, action: PayloadAction<string | null>) => {
      state.selectedVaultId = action.payload;
    },
    setSelectedMemberUserId: (state, action: PayloadAction<string | null>) => {
      state.selectedUserId = action.payload;
    },
    setVaultMemberModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },
    setVaultMemberModalMode: (state, action: PayloadAction<'add' | 'edit'>) => {
      state.modalMode = action.payload;
    },
    openVaultMemberEditModal: (
      state,
      action: PayloadAction<{ vaultId: string; userId: string }>
    ) => {
      state.selectedVaultId = action.payload.vaultId;
      state.selectedUserId = action.payload.userId;
      state.modalMode = 'edit';
      state.isModalOpen = true;
    },
    openVaultMemberAddModal: (state, action: PayloadAction<string>) => {
      state.selectedVaultId = action.payload;
      state.selectedUserId = null;
      state.modalMode = 'add';
      state.isModalOpen = true;
    },
    resetVaultMemberSelection: (state) => {
      state.selectedVaultId = null;
      state.selectedUserId = null;
      state.isModalOpen = false;
      state.modalMode = 'add';
    },
    clearVaultMemberError: (state) => {
      state.error = null;
      state.errors = { ...initialErrors };
    },
    clearVaultMemberItems: (state) => {
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getVaultMembers.pending, (state) => {
      state.operationLoading.list = true;
      state.error = null;
      state.errors.list = null;
    });
    builder.addCase(getVaultMembers.fulfilled, (state, action) => {
      state.operationLoading.list = false;
      state.items = Array.isArray(action.payload.items) ? action.payload.items : [];
      state.errors.list = null;
    });
    builder.addCase(getVaultMembers.rejected, (state, action) => {
      state.operationLoading.list = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Üyeler yüklenirken hata oluştu';
      state.error = msg;
      state.errors.list = msg;
    });

    builder.addCase(getVaultMember.pending, (state) => {
      state.operationLoading.get = true;
      state.errors.get = null;
    });
    builder.addCase(getVaultMember.fulfilled, (state, action) => {
      state.operationLoading.get = false;
      const item = action.payload;
      const idx = state.items.findIndex(
        (i) => i.vaultId === item.vaultId && i.userId === item.userId
      );
      if (idx >= 0) state.items[idx] = item;
      else state.items.push(item);
      state.errors.get = null;
    });
    builder.addCase(getVaultMember.rejected, (state, action) => {
      state.operationLoading.get = false;
      state.errors.get = typeof action.payload === 'string' ? action.payload : 'Üye yüklenirken hata oluştu';
    });

    builder.addCase(getVaultMemberMe.pending, (state) => {
      state.operationLoading.me = true;
      state.errors.me = null;
    });
    builder.addCase(getVaultMemberMe.fulfilled, (state, action) => {
      state.operationLoading.me = false;
      state.meVaults = Array.isArray(action.payload) ? action.payload : [];
      state.errors.me = null;
    });
    builder.addCase(getVaultMemberMe.rejected, (state, action) => {
      state.operationLoading.me = false;
      state.errors.me = typeof action.payload === 'string' ? action.payload : 'Kasa listesi yüklenirken hata oluştu';
    });

    builder.addCase(getVaultMemberMePending.pending, (state) => {
      state.operationLoading.mePending = true;
      state.errors.mePending = null;
    });
    builder.addCase(getVaultMemberMePending.fulfilled, (state, action) => {
      state.operationLoading.mePending = false;
      state.mePendingVaults = Array.isArray(action.payload) ? action.payload : [];
      state.errors.mePending = null;
    });
    builder.addCase(getVaultMemberMePending.rejected, (state, action) => {
      state.operationLoading.mePending = false;
      state.errors.mePending = typeof action.payload === 'string' ? action.payload : 'Bekleyen davetler yüklenirken hata oluştu';
    });

    builder.addCase(acceptVaultMemberInvite.fulfilled, (state, action) => {
      const vaultId = action.meta.arg;
      state.mePendingVaults = state.mePendingVaults.filter((v) => v.vaultId !== vaultId);
      const item = action.payload;
      if (item && !state.meVaults.some((v) => v.vaultId === item.vaultId)) {
        state.meVaults.push({
          vaultId: item.vaultId,
          vaultName: undefined,
          role: item.role,
          addedAt: item.addedAt,
        });
      }
    });

    builder.addCase(rejectVaultMemberInvite.fulfilled, (state, action) => {
      const vaultId = action.meta.arg;
      state.mePendingVaults = state.mePendingVaults.filter((v) => v.vaultId !== vaultId);
    });

    builder.addCase(createVaultMember.pending, (state) => {
      state.operationLoading.create = true;
      state.errors.create = null;
    });
    builder.addCase(createVaultMember.fulfilled, (state, action) => {
      state.operationLoading.create = false;
      const item = action.payload;
      if (
        item &&
        !state.items.some((i) => i.vaultId === item.vaultId && i.userId === item.userId)
      ) {
        state.items.push(item);
      }
      state.errors.create = null;
    });
    builder.addCase(createVaultMember.rejected, (state, action) => {
      state.operationLoading.create = false;
      state.errors.create = typeof action.payload === 'string' ? action.payload : 'Üye eklenirken hata oluştu';
    });

    builder.addCase(updateVaultMember.pending, (state) => {
      state.operationLoading.update = true;
      state.errors.update = null;
    });
    builder.addCase(updateVaultMember.fulfilled, (state, action) => {
      state.operationLoading.update = false;
      const { vaultId, userId, item } = action.payload;
      const idx = state.items.findIndex(
        (i) => i.vaultId === vaultId && i.userId === userId
      );
      if (idx >= 0 && item) state.items[idx] = item;
      state.errors.update = null;
    });
    builder.addCase(updateVaultMember.rejected, (state, action) => {
      state.operationLoading.update = false;
      state.errors.update = typeof action.payload === 'string' ? action.payload : 'Üye güncellenirken hata oluştu';
    });

    builder.addCase(deleteVaultMember.pending, (state) => {
      state.operationLoading.delete = true;
      state.errors.delete = null;
    });
    builder.addCase(deleteVaultMember.fulfilled, (state, action) => {
      state.operationLoading.delete = false;
      const { vaultId, userId } = action.payload;
      state.items = state.items.filter(
        (i) => !(i.vaultId === vaultId && i.userId === userId)
      );
      state.errors.delete = null;
    });
    builder.addCase(deleteVaultMember.rejected, (state, action) => {
      state.operationLoading.delete = false;
      state.errors.delete = typeof action.payload === 'string' ? action.payload : 'Üye kaldırılırken hata oluştu';
    });
  },
});

export const {
  setSelectedVaultIdForMembers,
  setSelectedMemberUserId,
  setVaultMemberModalOpen,
  setVaultMemberModalMode,
  openVaultMemberEditModal,
  openVaultMemberAddModal,
  resetVaultMemberSelection,
  clearVaultMemberError,
  clearVaultMemberItems,
} = vaultMemberSlice.actions;
export default vaultMemberSlice.reducer;
